import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { msalConfig, loginRequest } from './authConfig';
import HomePage from './pages/HomePage';
import NewRequest from './pages/NewRequest';
import TrackRequests from './pages/TrackRequests';
import UpdateRequest from './pages/UpdateRequest';
import authService from './services/AuthService';
import Dashboard from './pages/Dashboard/Dashboard';
import { RoleProvider } from './contexts/RoleContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import DataQualityPage from './pages/DataQualityPage';
import InventoryManagementPage from './pages/InventoryManagementPage';
import TEMUploadPage from './pages/TEMUploadPage';

// Initialize MSAL outside of the component and export for use in AuthService
export const msalInstance = new PublicClientApplication(msalConfig);

// Flag to track initialization
let msalInitialized = false;

// Initialize MSAL synchronously first to avoid race conditions
msalInstance.initialize().then(() => {
    console.log("MSAL initialized successfully");
    msalInitialized = true;
}).catch(error => {
    console.error("MSAL initialization failed", error);
});

// ============================================================================
// MSAL Event Callbacks for Authentication Tracking
// ============================================================================

msalInstance.addEventCallback((event) => {
    // Track successful login
    if (event.eventType === 'msal:loginSuccess' && event.payload.account) {
        authService.logAuthEvent('LOGIN_SUCCESS', {
            user_email: event.payload.account.username,
            user_name: event.payload.account.name
        });
    }

    // Track login failure
    if (event.eventType === 'msal:loginFailure') {
        authService.logAuthEvent('LOGIN_FAILURE', {
            error_code: event.error?.errorCode,
            error_message: event.error?.errorMessage,
            correlation_id: event.error?.correlationId
        });
    }

    // Track token acquisition failure
    if (event.eventType === 'msal:acquireTokenFailure') {
        authService.logAuthEvent('TOKEN_ACQUISITION_FAILED', {
            error_code: event.error?.errorCode,
            error_message: event.error?.errorMessage,
            correlation_id: event.error?.correlationId
        });
    }

    // Track SSO silent success
    if (event.eventType === 'msal:ssoSilentSuccess') {
        authService.logAuthEvent('SSO_SILENT_SUCCESS', {
            user_email: event.payload?.account?.username
        });
    }

    // Track SSO silent failure
    if (event.eventType === 'msal:ssoSilentFailure') {
        authService.logAuthEvent('SSO_SILENT_FAILURE', {
            error_code: event.error?.errorCode,
            error_message: event.error?.errorMessage
        });
    }
});

const RequireAuth = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isRoleValid, setIsRoleValid] = useState(false);
    const [showRoleError, setShowRoleError] = useState(false);

    // First, check authentication
    useEffect(() => {
        const checkAuth = async () => {
            if (accounts.length > 0) {
                try {
                    // Verify the session is still valid
                    const isActive = await authService.isSessionActive();
                    setIsAuthenticated(isActive);
                    
                    // Also check role from localStorage
                    const cachedRole = localStorage.getItem('odm_user_role');
                    if (cachedRole) {
                        const role = JSON.parse(cachedRole);
                        if (role.roleName !== "Guest") {
                            setIsRoleValid(true);
                        } else {
                            setShowRoleError(true);
                        }
                    }
                } catch (error) {
                    console.error("Session validation failed:", error);
                    setIsAuthenticated(false);
                }
            } else if (inProgress === 'none') {
                // No accounts and not in login process, redirect to login
                console.log("No account found, redirecting to login");
                instance.loginRedirect(loginRequest).catch((error) => {
                    console.error("Login failed:", error);
                });
            }
            setIsCheckingSession(false);
        };

        checkAuth();
    }, [accounts, instance, inProgress]);

    // Set up inactivity monitor once authenticated
    useEffect(() => {
        if (isAuthenticated) {
            // Monitor for 30 minutes of inactivity before auto-logout
            authService.setupInactivityMonitor(30 * 60 * 1000);
        }
    }, [isAuthenticated]);

    // Handle login redirect for role errors
    const handleRoleErrorClose = () => {
        setShowRoleError(false);
        // Force re-login to get fresh tokens and groups
        instance.logoutRedirect().then(() => {
            instance.loginRedirect(loginRequest);
        });
    };

    if (isCheckingSession) {
        return <div>Checking authentication...</div>;
    }

    // Show role error dialog if needed
    if (showRoleError) {
        return (
            <Dialog open={true} onClose={handleRoleErrorClose}>
                <DialogTitle sx={{ color: '#d32f2f' }}>Access Denied</DialogTitle>
                <DialogContent>
                    <Typography>
                        Your account doesn't have the required permissions to access this portal.
                        Please contact your administrator or try logging in again.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRoleErrorClose} color="primary" variant="contained">
                        Log in again
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return isAuthenticated && isRoleValid ? children : <div>Authenticating...</div>;
};

const App = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [showLoginSuccess, setShowLoginSuccess] = useState(false);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [msalError, setMsalError] = useState(false);

    useEffect(() => {
        // Create a function to safely handle the redirect
        const handleRedirect = async () => {
            try {
                // Wait for MSAL to be fully initialized if needed
                if (!msalInitialized) {
                    console.log("Waiting for MSAL to initialize...");
                    await msalInstance.initialize();
                    msalInitialized = true;
                }
                
                // Now safely handle the redirect
                const response = await msalInstance.handleRedirectPromise();

                if (response) {
                    console.log("Successfully logged in:", response);
                    // Set up token renewal after successful login
                    if (response.account) {
                        authService.scheduleTokenRenewal(response.expiresOn);

                        // Log successful login
                        await authService.logAuthEvent('LOGIN_SUCCESS', {
                            user_email: response.account.username,
                            user_name: response.account.name
                        });

                        // Show login success message
                        setLoggedInUser(response.account.username);
                        setShowLoginSuccess(true);

                        // Hide message after 3 seconds
                        setTimeout(() => {
                            setShowLoginSuccess(false);
                        }, 3000);
                    }
                } else {
                    console.log("No redirect response");
                }
            } catch (error) {
                console.error("Error handling redirect:", error);
                setMsalError(true);
            } finally {
                setIsLoading(false);
            }
        };
        
        handleRedirect();
    }, []);

    const handleMsalErrorClose = () => {
        setMsalError(false);
        window.location.reload(); // Reload page to reinitialize MSAL
    };

    if (isLoading) {
        return <div>Loading authentication...</div>;
    }

    return (
        <MsalProvider instance={msalInstance}>
            {/* MSAL Error Dialog */}
            <Dialog open={msalError} onClose={handleMsalErrorClose}>
                <DialogTitle>Authentication Error</DialogTitle>
                <DialogContent>
                    <Typography>
                        There was an error with authentication. Please reload the page to try again.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMsalErrorClose} color="primary">
                        Reload
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Login success toast message */}
            {showLoginSuccess && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    zIndex: 1000,
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                }}>
                    Successfully logged in as {loggedInUser}
                </div>
            )}
            <RoleProvider>
                <Router>
                    <Routes>
                        <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
                        <Route path="/new-request" element={<RequireAuth><NewRequest /></RequireAuth>} />
                        <Route path="/track-requests" element={<RequireAuth><TrackRequests /></RequireAuth>} />
                        <Route path="/update-request/:requestId" element={<RequireAuth><UpdateRequest /></RequireAuth>} />
                        <Route path="/data-quality" element={<RequireAuth><DataQualityPage /></RequireAuth>} /> {/* Add this route */}
                        <Route path="/inventory-management" element={<RequireAuth><InventoryManagementPage /></RequireAuth>} /> {/* Add this route */}
                        <Route path="/tem-upload" element={<RequireAuth><TEMUploadPage /></RequireAuth>} />
                        {/* Handle all other routes */}
                        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />

                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </Router>
            </RoleProvider>
        </MsalProvider>
    );
};

export default App;